// The typed API client (E12-S1-T1). The web app holds no token: the session is the API's httpOnly
// cookie (E15-S4), so every request is a same-origin fetch that carries it. Three rules hold for
// every call:
//
//   - The path is relative to this origin. An absolute or protocol-relative address is refused
//     before any request is made, so a cookie can never ride to another host.
//   - A request that changes state (anything but GET and HEAD) carries X-CSRF-Token, fetched once
//     from GET /auth/csrf and kept for the session (E15-S4-T4). A 401 or 403 drops the kept value,
//     so the next state-changing request fetches a fresh one.
//   - Every answer is parsed with its schema at this boundary. An answer that does not match is a
//     ValidationError, never an object with undefined fields; a page only sees parsed data.
//
// Errors are the typed ApiErrors in errors.ts. A 401 from any request also calls onUnauthorised,
// which flips the app to signed-out (src/auth/session-state.tsx).
import { Principal } from "@waypoint/contracts";
import { z } from "zod";
import {
  ConflictError,
  ForbiddenError,
  NotFoundError,
  OfflineError,
  ServerError,
  UnauthorisedError,
  ValidationError,
  type ApiErrorInit,
  type Refusal,
  type ValidationIssue,
} from "./errors.js";

// zod 4 probes for eval with new Function on first parse; under the static service's script-src
// that probe is a CSP violation (apps/sim found the same). Plain closures only.
z.config({ jitless: true });

/**
 * GET /auth/csrf's answer. The contracts define no REST shapes (backlog/tasks/E12.yaml); E15-S4-T4
 * owns this route and the shape here is the client's reading of it, kept strict so a change shows.
 */
export const CsrfAnswer = z.strictObject({ token: z.string().min(1) });

/**
 * The one failure body the API sends (E15-S4): { code, message, fields? }. Only the code and the
 * field paths of a refused request are kept; a body
 * that is not this shape (a proxy's HTML page, say) leaves the error without a code.
 */
export const ApiErrorBody = z.object({
  code: z.string().min(1),
  message: z.string(),
  fields: z.array(z.string()).optional(),
});

const RefusalItem = z.object({
  code: z.string().min(1),
  usedSeconds: z.number().int().optional(),
  budgetSeconds: z.number().int().optional(),
  previousUsedSeconds: z.number().int().optional(),
});

/**
 * A 422 body that lists refusals: { errors: [{ code, usedSeconds?, ... }] }, or names one, as the render
 * and approve routes do: { refusal: { code, ... } }. Only the codes and the figures are kept; a segment
 * id or any other field is dropped here.
 */
const RefusalBody = z.union([
  z.object({ errors: z.array(RefusalItem) }).transform((b) => b.errors),
  z.object({ refusal: RefusalItem }).transform((b) => [b.refusal]),
]);

export type Method = "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiClientOptions {
  /** The fetch to use; the tests pass a stub. Defaults to the global fetch. */
  readonly fetch?: typeof fetch;
  /** Called once for every 401, before the request rejects. */
  readonly onUnauthorised?: (err: UnauthorisedError) => void;
}

export interface ApiClient {
  request<S extends z.ZodType>(
    method: Method,
    path: string,
    schema: S,
    body?: unknown,
  ): Promise<z.output<S>>;
  get<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>>;
  post<S extends z.ZodType>(path: string, schema: S, body?: unknown): Promise<z.output<S>>;
  put<S extends z.ZodType>(path: string, schema: S, body?: unknown): Promise<z.output<S>>;
  patch<S extends z.ZodType>(path: string, schema: S, body?: unknown): Promise<z.output<S>>;
  delete<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>>;
  /**
   * GET a binary answer (a draft's still, E31-S6-T2): its bytes and declared type, with the same
   * same-origin rule and typed errors as every other call. Nothing is parsed.
   */
  bytes(path: string): Promise<{ bytes: Uint8Array; contentType: string }>;
  /** Who is signed in: GET /auth/me, parsed as a Principal (E15-S4-T3). */
  me(): Promise<Principal>;
}

const SAFE_METHODS: ReadonlySet<Method> = new Set(["GET", "HEAD"]);

/** A path on this origin: one leading slash, not two, no backslash, no scheme. */
export function isSameOriginPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.includes("\\");
}

function issuesOf(error: z.ZodError): ValidationIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join("."),
    code: issue.code,
  }));
}

async function errorBody(res: Response): Promise<{
  code?: string | undefined;
  fields?: readonly string[] | undefined;
  refusals: readonly Refusal[];
}> {
  try {
    const body: unknown = JSON.parse(await res.text());
    const parsed = ApiErrorBody.safeParse(body);
    const refused = res.status === 422 ? RefusalBody.safeParse(body) : undefined;
    return {
      ...(parsed.success ? { code: parsed.data.code, fields: parsed.data.fields } : {}),
      refusals: refused?.success === true ? refused.data : [],
    };
  } catch {
    return { refusals: [] };
  }
}

function failure(status: number, init: ApiErrorInit, refusals: readonly Refusal[]): Error {
  const where = `${init.method} ${init.path}`;
  switch (status) {
    case 401:
      return new UnauthorisedError(`${where}: not signed in`, init);
    case 403:
      return new ForbiddenError(`${where}: forbidden`, init);
    case 404:
      return new NotFoundError(`${where}: not found`, init);
    case 409:
      return new ConflictError(`${where}: the resource's state refused the request`, init);
    case 400:
    case 422:
      return new ValidationError(`${where}: the request was refused`, {
        ...init,
        source: "request",
        refusals,
      });
    default:
      return new ServerError(`${where}: answered ${String(status)}`, init);
  }
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  let csrf: Promise<string> | undefined;

  async function send(
    method: Method,
    path: string,
    body: unknown,
    accept = "application/json",
  ): Promise<Response> {
    const headers: Record<string, string> = { Accept: accept };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (!SAFE_METHODS.has(method)) headers["X-CSRF-Token"] = await csrfToken();
    try {
      return await doFetch(path, {
        method,
        credentials: "same-origin",
        headers,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      throw new OfflineError(`${method} ${path}: no answer`, { method, path, cause });
    }
  }

  async function request<S extends z.ZodType>(
    method: Method,
    path: string,
    schema: S,
    body?: unknown,
  ): Promise<z.output<S>> {
    if (!isSameOriginPath(path))
      throw new TypeError(`The API client only requests paths on this origin; got "${path}"`);
    const res = await send(method, path, body);
    const init: ApiErrorInit = { method, path, status: res.status };
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) csrf = undefined;
      const { refusals, ...named } = await errorBody(res);
      const err = failure(res.status, { ...init, ...named }, refusals);
      if (err instanceof UnauthorisedError) options.onUnauthorised?.(err);
      throw err;
    }
    let data: unknown;
    try {
      const text = await res.text();
      data = text === "" ? undefined : JSON.parse(text);
    } catch (cause) {
      throw new ValidationError(`${method} ${path}: the answer is not JSON`, {
        ...init,
        cause,
        source: "response",
      });
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success)
      throw new ValidationError(`${method} ${path}: the answer does not match its contract`, {
        ...init,
        source: "response",
        issues: issuesOf(parsed.error),
      });
    return parsed.data;
  }

  async function bytes(path: string): Promise<{ bytes: Uint8Array; contentType: string }> {
    if (!isSameOriginPath(path))
      throw new TypeError(`The API client only requests paths on this origin; got "${path}"`);
    const res = await send("GET", path, undefined, "*/*");
    const init: ApiErrorInit = { method: "GET", path, status: res.status };
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) csrf = undefined;
      const { refusals, ...named } = await errorBody(res);
      const err = failure(res.status, { ...init, ...named }, refusals);
      if (err instanceof UnauthorisedError) options.onUnauthorised?.(err);
      throw err;
    }
    try {
      return {
        bytes: new Uint8Array(await res.arrayBuffer()),
        contentType: res.headers.get("Content-Type") ?? "",
      };
    } catch (cause) {
      throw new OfflineError(`GET ${path}: the answer was cut short`, { ...init, cause });
    }
  }

  function csrfToken(): Promise<string> {
    csrf ??= request("GET", "/auth/csrf", CsrfAnswer).then(
      (answer) => answer.token,
      (err: unknown) => {
        csrf = undefined;
        throw err;
      },
    );
    return csrf;
  }

  return {
    request,
    get: (path, schema) => request("GET", path, schema),
    post: (path, schema, body) => request("POST", path, schema, body),
    put: (path, schema, body) => request("PUT", path, schema, body),
    patch: (path, schema, body) => request("PATCH", path, schema, body),
    delete: (path, schema) => request("DELETE", path, schema),
    bytes,
    me: () => request("GET", "/auth/me", Principal),
  };
}
